import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { AssetStateMessage, ContentAsset } from '@r1-compass-library/models';
import { AssetStore } from '@r1-compass-library/store/asset.store';
import { Observable, concat, exhaustMap } from 'rxjs';
import { tap } from 'rxjs/operators';
import * as uuid from 'uuid';
import { ContentAssetService } from '@r1-compass-library/services/content-asset.service';
import { exhaustOp, rxMutation } from '@angular-architects/ngrx-toolkit';

export interface SelectedContentAssetState {
    asset: ContentAsset | undefined;
    isLoading: boolean;
    error: string | undefined;
    message: AssetStateMessage | undefined;
}

export const initialState: SelectedContentAssetState = {
    asset: undefined,
    isLoading: false,
    error: undefined,
    message: undefined,
};

@Injectable()
export class SelectedContentAssetStore extends AssetStore<ContentAsset, SelectedContentAssetState> {
    readonly #contentAssetService = inject(ContentAssetService);

    asset$ = this.select(state => state.asset);
    asset = this.selectSignal(state => state.asset);
    message$ = this.select(state => state.message);

    assetTypeName = 'Content Asset';

    constructor() {
        super(initialState);
    }

    reset = this.updater(state => ({
        ...initialState,
    }));

    setIsLoading = this.updater(state => ({
        ...state,
        isLoading: true,
    }));

    setError = this.updater((state, error: HttpErrorResponse) => ({
        ...state,
        isLoading: false,
        error: error.message,
    }));

    setMessage = this.updater((state, message: AssetStateMessage) => ({
        ...state,
        isLoading: false,
        message: message,
    }));

    resetMessage = this.updater(state => ({
        ...state,
        isLoading: false,
        message: undefined,
    }));

    setAsset = this.updater((state, asset: ContentAsset) => {
        asset = this.setRuleIds(asset);
        return {
            ...state,
            asset,
            isLoading: false,
        };
    });

    getAsset = this.effect((id$: Observable<{ id: number }>) => {
        return id$.pipe(
            tap(() => this.setIsLoading()),
            exhaustMap(path => {
                const userDivisionCodes: string[] = this.usersAvailableDivisions().map(
                    availableDivision => availableDivision.divisionCode
                );
                return this.#contentAssetService.search({ id: path.id, divisions: userDivisionCodes }).pipe(
                    tapResponse({
                        next: assets => this.handleSearchResponse(assets, this.setAsset, this.setMessage),
                        error: (err: HttpErrorResponse) => console.error(err),
                    })
                );
            })
        );
    });

    saveAsset = rxMutation({
        operation: (args: { asset: Partial<ContentAsset>; unlinkImporter?: boolean }) => {
            const restCalls: Observable<ContentAsset>[] = [];
            restCalls.push(
                args.asset.id
                    ? this.#contentAssetService.update(args.asset.id, args.asset)
                    : this.#contentAssetService.create(args.asset)
            );
            if (args.unlinkImporter && args.asset.id) {
                restCalls.push(this.#contentAssetService.unlinkImporter(args.asset.id));
            }
            return concat(...restCalls);
        },
        operator: exhaustOp,
        onSuccess: asset => this.setAsset(asset),
        onError: err => console.error(err),
    });

    private setRuleIds(asset: ContentAsset): ContentAsset {
        return {
            ...asset,
            rules: asset.rules?.map(rule => {
                return {
                    ...rule,
                    id: rule.id ?? uuid.v4().substring(0, 8),
                };
            }),
        };
    }
}
